import { router } from '@inertiajs/react'
import StudentLayout from '../components/StudentLayout'
import Heading from '@arianna/design-system/Heading'
import Text from '@arianna/design-system/Text'
import { BundleCard, CurrentBundleCard } from '@arianna/reader-ui/library'
import type {
  BundleCardBundle,
  CurrentBundleCardBundle,
  CurrentBundleCardStats,
} from '@arianna/reader-ui/library'

// ---------------------------------------------------------------------------
// Types (mirrored from LibraryQuery / API contract)
// ---------------------------------------------------------------------------

interface LibraryEntry {
  manual_id: number
  slug: string
  title: string
  subtitle: string | null
  author: string
  published_at: string | null
  enrolled: boolean
  manual_version_id: number | null
  /** GAP-09: sub-para completion counts for the progress bar (enrolled only) */
  completed_count: number
  total_count: number
}

interface Props {
  entries: LibraryEntry[]
}

// ---------------------------------------------------------------------------
// Data mapping — LibraryEntry → shared component props
// ---------------------------------------------------------------------------

/**
 * Maps a LibraryEntry to BundleCardBundle props.
 * Fields not available in the web data model (daysToExam, examDate, prof) are
 * set to null — the component renders them conditionally.
 */
function toBundleCardBundle(entry: LibraryEntry): BundleCardBundle {
  const total = entry.total_count
  const read = entry.completed_count
  return {
    code: entry.slug,
    title: entry.title,
    subtitle: entry.subtitle,
    cat: entry.author,
    version: entry.published_at ? String(new Date(entry.published_at).getFullYear()) : '1',
    daysToExam: null,
    paragraphsRead: read,
    paragraphsTotal: total,
    coverage: total > 0 ? read / total : 0,
  }
}

/**
 * Maps a LibraryEntry to CurrentBundleCardBundle props.
 * Fields not available in the web data model are set to null.
 */
function toCurrentBundleCardBundle(entry: LibraryEntry): CurrentBundleCardBundle {
  return {
    code: entry.slug,
    title: entry.title,
    subtitle: entry.subtitle,
    cat: entry.author,
    version: entry.published_at ? String(new Date(entry.published_at).getFullYear()) : '1',
    daysToExam: null,
    examDate: null,
    prof: null,
  }
}

function toCurrentBundleCardStats(entry: LibraryEntry): CurrentBundleCardStats {
  return {
    read: entry.completed_count,
    total: entry.total_count,
  }
}

// ---------------------------------------------------------------------------
// EmptyState
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-16) var(--space-6)',
        textAlign: 'center',
        gap: 'var(--space-4)',
      }}
    >
      {/* Bookshelf icon */}
      <svg
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="none"
        stroke="var(--ink-4)"
        strokeWidth="1.5"
        strokeLinecap="round"
      >
        <path d="M8 8h10v32H8z" />
        <path d="M22 8h7v32h-7z" />
        <path d="M33 10l8 2-4 26-8-2z" />
      </svg>

      <Heading variant="h3" style={{ color: 'var(--ink-2)' }}>
        Nessun manuale disponibile
      </Heading>
      <Text variant="body" style={{ color: 'var(--ink-3)', maxWidth: 320, display: 'block' }}>
        Non hai ancora accesso a nessun manuale. Contatta il tuo docente per ottenere l'accesso.
      </Text>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Library page — mirrors iPad LibraryOverlay layout
// ---------------------------------------------------------------------------

export default function Library({ entries }: Props) {
  const enrolled = entries.filter((e) => e.enrolled)
  const notEnrolled = entries.filter((e) => !e.enrolled)

  return (
    <StudentLayout>
      <div
        style={{
          maxWidth: 900,
          margin: '0 auto',
          padding: 'var(--space-10) var(--space-6)',
        }}
      >
        {/* Header — mirrors iPad: eyebrow + serif title */}
        <div style={{ marginBottom: 'var(--space-8)' }}>
          <Heading variant="eyebrow" style={{ marginBottom: 'var(--space-2)' }}>
            Libreria
          </Heading>
          <Heading variant="panel" style={{ color: 'var(--ink)' }}>
            I tuoi manuali
          </Heading>
        </div>

        {entries.length === 0 ? (
          <EmptyState />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-10)' }}>
            {/* In studio — "Current bundle" section, mirrors iPad CurrentBundleCard */}
            {enrolled.length > 0 && (
              <section>
                <Heading
                  variant="eyebrow"
                  style={{ marginBottom: 'var(--space-4)', color: 'var(--ink-3)' }}
                >
                  In studio adesso
                </Heading>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                  {enrolled.map((entry, i) => (
                    <div
                      key={entry.manual_id}
                      className="arianna-card-enter"
                      style={{ animationDelay: `${i * 60}ms` }}
                    >
                      <CurrentBundleCard
                        bundle={toCurrentBundleCardBundle(entry)}
                        stats={toCurrentBundleCardStats(entry)}
                        onContinue={() => {
                          if (entry.manual_version_id != null) {
                            router.visit(`/read/${entry.manual_version_id}`)
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Disponibili — "Altri bundle" section, mirrors iPad BundleCard grid */}
            {notEnrolled.length > 0 && (
              <section>
                <Heading
                  variant="eyebrow"
                  style={{ marginBottom: 'var(--space-4)', color: 'var(--ink-3)' }}
                >
                  Disponibili
                </Heading>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 'var(--space-4)',
                  }}
                >
                  {notEnrolled.map((entry, i) => (
                    <div
                      key={entry.manual_id}
                      className="arianna-card-enter"
                      style={{ animationDelay: `${i * 60}ms` }}
                    >
                      <BundleCard
                        bundle={toBundleCardBundle(entry)}
                        onClick={() => router.visit(`/manuals/${entry.manual_id}/preview`)}
                      />
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </StudentLayout>
  )
}
